import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabase/config';
import { checkInService } from '../../services/checkInService';
import { generateShortId } from '../../utils/customer';
import { Mail, X } from 'lucide-react';

import LandingView from './checkin/LandingView';
import CheckInForm from './checkin/CheckInForm';
import SuccessView from './checkin/SuccessView';

const PENDING_KEY = 'pending_checkin';

const CheckIn = () => {
    const { tenantId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [view, setView] = useState('loading');
    const [debugInfo, setDebugInfo] = useState('');
    const [status, setStatus] = useState('idle');
    const [tenant, setTenant] = useState(null);
    const [tableNum] = useState(() => new URLSearchParams(window.location.search).get('table'));
    const [menuData, setMenuData] = useState(null);
    const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', phoneCode: '+90', password: '', confirmPassword: '' });
    const [loginIdentifier, setLoginIdentifier] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [checkInMode, setCheckInMode] = useState('register');
    const [result, setResult] = useState(null);
    const [actualId, setActualId] = useState(null);
    const [formError, setFormError] = useState('');

    // OTP state
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otpCode, setOtpCode] = useState(['', '', '', '']);
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [otpResendCooldown, setOtpResendCooldown] = useState(0);
    const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
    const cooldownRef = useRef(null);

    const countries = [
        { name: 'Turkiye', code: '+90', flag: '🇹🇷' },
        { name: 'Azerbaycan', code: '+994', flag: '🇦🇿' },
        { name: 'Almanya', code: '+49', flag: '🇩🇪' },
        { name: 'Ingiltere', code: '+44', flag: '🇬🇧' },
        { name: 'ABD', code: '+1', flag: '🇺🇸' }
    ];

    // OTP cooldown timer
    useEffect(() => {
        if (otpResendCooldown > 0) {
            cooldownRef.current = setTimeout(() => setOtpResendCooldown(c => c - 1), 1000);
            return () => clearTimeout(cooldownRef.current);
        }
    }, [otpResendCooldown]);

    // Auto-focus first OTP input when modal opens
    useEffect(() => {
        if (showOtpModal && otpRefs[0].current) {
            setTimeout(() => otpRefs[0].current?.focus(), 100);
        }
    }, [showOtpModal]);

    const sendOtp = useCallback(async () => {
        const email = formData.email.trim().toLowerCase();
        const res = await fetch('/api/send-otp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, type: 'customer' })
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || 'OTP gönderilemedi');
        }
        setOtpResendCooldown(60);
    }, [formData.email]);

    const handleOtpChange = (index, value) => {
        if (!/^\d?$/.test(value)) return;
        const newCode = [...otpCode];
        newCode[index] = value;
        setOtpCode(newCode);
        setOtpError('');

        if (value && index < 3) {
            otpRefs[index + 1].current?.focus();
        }
        if (value && index === 3) {
            const fullCode = newCode.join('');
            if (fullCode.length === 4) verifyOtp(fullCode);
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
            otpRefs[index - 1].current?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
        if (!pasted) return;
        const newCode = ['', '', '', ''];
        for (let i = 0; i < pasted.length; i++) newCode[i] = pasted[i];
        setOtpCode(newCode);
        setOtpError('');
        if (pasted.length === 4) {
            verifyOtp(pasted);
        } else {
            otpRefs[Math.min(pasted.length, 3)].current?.focus();
        }
    };

    const verifyOtp = async (code) => {
        setOtpLoading(true);
        setOtpError('');
        try {
            const email = formData.email.trim().toLowerCase();
            const res = await fetch('/api/verify-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, code })
            });
            const data = await res.json();
            if (data.verified) {
                setShowOtpModal(false);
                await completeRegistration();
            } else if (data.expired) {
                setOtpError('Kodun süresi dolmuş. Lütfen yeni kod isteyin.');
                setOtpCode(['', '', '', '']);
                otpRefs[0].current?.focus();
            } else {
                setOtpError('Girdiğiniz kod yanlış. Tekrar deneyin.');
                setOtpCode(['', '', '', '']);
                otpRefs[0].current?.focus();
            }
        } catch (err) {
            setOtpError('Doğrulama sırasında bir hata oluştu.');
        } finally {
            setOtpLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (otpResendCooldown > 0) return;
        setOtpError('');
        try {
            await sendOtp();
        } catch (err) {
            setOtpError(err.message);
        }
    };

    const processPendingRegistration = async (tenantObj) => {
        try {
            const pending = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
            if (!pending || pending.tenantId !== tenantObj.id) return false;

            const res = await checkInService.registerCustomer(tenantObj.id, pending.formData, tenantObj.data);

            // Set global identity
            localStorage.setItem('crm_global_customer_id', res.id);

            localStorage.removeItem(PENDING_KEY);
            navigate(`/portal/${tenantObj.data.slug || tenantObj.id}${tableNum ? `?table=${tableNum}` : ''}`, { replace: true });
            return true;
        } catch (err) {
            console.error('Pending registration error:', err);
            localStorage.removeItem(PENDING_KEY);
            return false;
        }
    };

    useEffect(() => {
        if (!tenantId) return;
        const init = async () => {
            try {
                // BYPASS BROKEN API - GO DIRECT TO SUPABASE
                const tenantObj = await checkInService.fetchTenant(tenantId);

                if (!tenantObj || tenantObj.error) {
                    setDebugInfo(`Tenant not found: ${tenantId}`);
                    setView('error');
                    return;
                }

                if (tenantObj.data) {
                    const mapped = {
                        ...tenantObj.data,
                        loyaltyRules: tenantObj.data.loyalty_rules,
                        theme: tenantObj.data.theme,
                        tables: tenantObj.data.tables
                    };
                    setTenant(mapped);
                }
                setActualId(tenantObj.id);

                const menu = await checkInService.fetchMenu(tenantObj.id, tenantObj.data?.active_menu_id);
                setMenuData(menu);

                // Check if user is already a member of this cafe via DB (not localStorage)
                const globalId = localStorage.getItem('crm_global_customer_id');
                const isGlobalUUID = globalId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(globalId);

                if (isGlobalUUID) {
                    const { data: globalRec } = await supabase.from('customers').select('email').eq('id', globalId).maybeSingle();
                    if (globalRec?.email) {
                        const { data: localMatch } = await supabase
                            .from('customers')
                            .select('id')
                            .eq('tenant_id', tenantObj.id)
                            .eq('email', globalRec.email)
                            .maybeSingle();
                        if (localMatch) {
                            navigate(`/portal/${tenantObj.data.slug || tenantObj.id}${tableNum ? `?table=${tableNum}` : ''}`, { replace: true });
                            return;
                        }
                    }
                }

                const pending = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
                if (pending && pending.tenantId === tenantObj.id) {
                    setFormData({ ...formData, email: pending.formData.email, phone: pending.formData.phone });
                    setView('verify-email');
                    return;
                }
            } catch (err) {
                console.error("Initialization error:", err);
            }
            setView('landing');
        };
        init();
    }, [tenantId]);

    const handleLogin = async (e) => {
        if (e) e.preventDefault();
        setFormError('');
        setStatus('loading');

        try {
            const identifier = loginIdentifier.trim().toLowerCase();
            const isEmail = identifier.includes('@');

            let query;
            if (isEmail) {
                query = supabase.from('customers').select('*').eq('tenant_id', actualId).eq('email', identifier);
            } else {
                const cleanPhone = identifier.replace(/[^0-9]/g, '');
                // Check if phone matches with or without country code
                query = supabase.from('customers').select('*').eq('tenant_id', actualId).like('phone', `%${cleanPhone}`);
            }

            const { data: rows, error: fetchErr } = await query;
            if (fetchErr) throw fetchErr;

            if (rows && rows.length > 0) {
                const customerData = rows[0];

                // Check Password
                if (customerData.password && customerData.password !== loginPassword) {
                    setFormError(t('login.invalidPassword', 'Girdiğiniz şifre yanlış.'));
                    setStatus('idle');
                    return;
                }

                const res = await checkInService.processCheckIn(actualId, customerData, tenant);
                setResult({ ...res, name: customerData.first_name, isNew: false });

                // Set global identity
                localStorage.setItem('crm_global_customer_id', customerData.id);

                setView('success');
            } else {
                // If not found in current cafe, search GLOBALLY across all tenants
                let globalQuery;
                if (isEmail) {
                    globalQuery = supabase.from('customers').select('*').eq('email', identifier);
                } else {
                    const cleanPhone = identifier.replace(/[^0-9]/g, '');
                    globalQuery = supabase.from('customers').select('*').like('phone', `%${cleanPhone}`);
                }

                const { data: globalRows } = await globalQuery;

                if (globalRows && globalRows.length > 0) {
                    const globalUser = globalRows[0];

                    // Check Password against global account
                    if (globalUser.password && globalUser.password !== loginPassword) {
                        setFormError(t('login.invalidPassword', 'Girdiğiniz şifre yanlış.'));
                        setStatus('idle');
                        return;
                    }

                    // Found globally! Now "Join" them to this cafe automatically
                    const rules = tenant?.loyalty_rules || tenant?.loyaltyRules || {};
                    const welcomePoints = rules.welcomePoints || 0;

                    const shortId = generateShortId();
                    const { data: newLocalCustomer, error: linkErr } = await supabase
                        .from('customers')
                        .insert({
                            first_name: globalUser.first_name,
                            last_name: globalUser.last_name,
                            email: globalUser.email,
                            phone: globalUser.phone,
                            tenant_id: actualId,
                            short_id: shortId,
                            points: welcomePoints,
                            total_points: welcomePoints,
                            point_earnings: welcomePoints > 0 ? [{ p: welcomePoints, t: new Date().toISOString() }] : [],
                            password: globalUser.password, // Preserve global password
                            created_at: new Date().toISOString()
                        })
                        .select()
                        .single();

                    if (linkErr) throw linkErr;

                    // Now process check-in for the newly created local record
                    const res = await checkInService.processCheckIn(actualId, newLocalCustomer, tenant);
                    setResult({ ...res, name: newLocalCustomer.first_name, isNew: true });

                    // Set global identity
                    localStorage.setItem('crm_global_customer_id', newLocalCustomer.id);

                    setView('success');
                } else {
                    setFormError(t('login.userNotFound', 'Kullanıcı bulunamadı. Lütfen kayıt olun.'));
                    setStatus('idle');
                }
            }
        } catch (err) {
            console.error(err);
            setFormError('Bir hata oluştu. Lütfen tekrar deneyin.');
            setStatus('idle');
        }
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setFormError('');
        setStatus('loading');
        try {
            if (formData.password !== formData.confirmPassword) {
                setFormError('Girdiğiniz şifreler birbiriyle eşleşmedi.');
                setStatus('idle');
                return;
            }
            const fullPhone = formData.phoneCode + formData.phone.replace(/\D/g, '');

            // Check if phone already exists in this tenant
            const { data: existing } = await supabase.from('customers').select('*').eq('tenant_id', actualId).eq('phone', fullPhone);

            if (existing && existing.length > 0) {
                setFormError('Bu telefon numarası ile zaten bir kaydınız bulunuyor. Lütfen giriş yapın.');
                setCheckInMode('login');
                setStatus('idle');
                return;
            }

            // Send OTP and show modal
            await sendOtp();
            setOtpCode(['', '', '', '']);
            setOtpError('');
            setShowOtpModal(true);
            setStatus('idle');

        } catch (err) {
            console.error("Submit Error:", err);
            setFormError(err.message || 'Kayıt sırasında bir hata oluştu.');
            setStatus('idle');
        }
    };

    const completeRegistration = async () => {
        setStatus('loading');
        try {
            const email = formData.email.trim().toLowerCase();
            const fullPhone = formData.phoneCode + formData.phone.replace(/\D/g, '');

            const res = await checkInService.registerCustomer(actualId, { ...formData, email, phone: fullPhone }, tenant);

            // Set global identity
            localStorage.setItem('crm_global_customer_id', res.id);

            setResult({ ...res, name: formData.firstName, isNew: true });
            setView('success');

        } catch (err) {
            console.error("Registration Error:", err);
            setFormError('Kayıt sırasında bir hata oluştu.');
            setStatus('idle');
        }
    };

    const otpModal = showOtpModal && (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '1rem',
                animation: 'fadeIn 0.2s ease-out'
            }}
            onClick={(e) => { if (e.target === e.currentTarget) { setShowOtpModal(false); } }}
        >
            <div className="card glass" style={{
                maxWidth: '400px', width: '100%', padding: '2.5rem',
                position: 'relative', textAlign: 'center',
                animation: 'slideUp 0.3s ease-out'
            }}>
                <button
                    onClick={() => setShowOtpModal(false)}
                    style={{
                        position: 'absolute', right: '1rem', top: '1rem',
                        background: 'transparent', border: 'none',
                        color: 'var(--text-muted)', cursor: 'pointer'
                    }}
                >
                    <X size={20} />
                </button>

                <div style={{
                    width: '56px', height: '56px', borderRadius: '50%',
                    background: 'rgba(111, 78, 55, 0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 1.5rem'
                }}>
                    <Mail size={28} color="#6f4e37" />
                </div>

                <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.5rem' }}>
                    Doğrulama Kodu
                </h2>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '2rem' }}>
                    <strong>{formData.email}</strong> adresine gönderilen 4 haneli kodu girin.
                </p>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '1.5rem' }}>
                    {otpCode.map((digit, i) => (
                        <input
                            key={i}
                            ref={otpRefs[i]}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={e => handleOtpChange(i, e.target.value)}
                            onKeyDown={e => handleOtpKeyDown(i, e)}
                            onPaste={i === 0 ? handleOtpPaste : undefined}
                            disabled={otpLoading}
                            style={{
                                width: '56px', height: '64px',
                                textAlign: 'center', fontSize: '1.5rem', fontWeight: 700,
                                borderRadius: '12px',
                                border: `2px solid ${otpError ? '#ef4444' : digit ? '#6f4e37' : 'var(--border)'}`,
                                background: 'var(--bg-main)',
                                outline: 'none',
                                transition: 'border-color 0.2s'
                            }}
                        />
                    ))}
                </div>

                {otpLoading && (
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>
                        Doğrulanıyor...
                    </p>
                )}

                {otpError && (
                    <div style={{
                        padding: '0.75rem', background: '#fee2e2', color: '#ef4444',
                        borderRadius: '10px', fontSize: '0.8125rem', marginBottom: '1rem'
                    }}>
                        {otpError}
                    </div>
                )}

                <button
                    onClick={handleResendOtp}
                    disabled={otpResendCooldown > 0 || otpLoading}
                    style={{
                        background: 'transparent', border: 'none',
                        color: otpResendCooldown > 0 ? 'var(--text-muted)' : '#6f4e37',
                        fontWeight: 600, fontSize: '0.875rem', cursor: otpResendCooldown > 0 ? 'default' : 'pointer',
                        marginBottom: '0.5rem'
                    }}
                >
                    {otpResendCooldown > 0 ? `Tekrar gönder (${otpResendCooldown}s)` : 'Kodu tekrar gönder'}
                </button>

                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    Spam/gereksiz klasörünü kontrol etmeyi unutmayın.
                </p>
            </div>
            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );

    if (view === 'loading') return <div style={{ textAlign: 'center', padding: '5rem' }}>Yukleniyor...</div>;
    if (view === 'error') return <div style={{ textAlign: 'center', padding: '5rem' }}>{debugInfo}</div>;
    if (view === 'verify-email') return <div style={{ textAlign: 'center', padding: '5rem' }}>E-postanizi kontrol edin: {formData.email}</div>;
    if (view === 'success') return (
        <SuccessView
            result={result}
            tenant={tenant}
            menuData={menuData}
            onGoToPortal={() => navigate(`/portal/${tenant?.slug || actualId}${tableNum ? `?table=${tableNum}` : ''}`)}
            t={t}
        />
    );
    if (view === 'form') return (
        <>
        {otpModal}
        <CheckInForm
            mode={checkInMode}
            setMode={setCheckInMode}
            formData={formData}
            setFormData={setFormData}
            loginIdentifier={loginIdentifier}
            setLoginIdentifier={setLoginIdentifier}
            loginPassword={loginPassword}
            setLoginPassword={setLoginPassword}
            onBack={() => setView('landing')}
            onSubmit={handleSubmit}
            onLogin={handleLogin}
            tenant={tenant}
            t={t}
            formError={formError}
            countries={countries}
            status={status}
        />
        </>
    );

    return <LandingView tenant={tenant} menuData={menuData} onStartVisit={() => setView('form')} t={t} />;
};

export default CheckIn;
